import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { authAxios } from '../contexts/AuthContext';
import { ChevronLeft, Package, MapPin, Phone, FileText, Clock, Check, Truck, X, RotateCcw, AlertCircle, RefreshCw, Star, ShieldCheck, Search, Download, AlertTriangle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { t } from '../lib/translations';
import { toast } from 'sonner';
import OrderBill from '../components/OrderBill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending:    { icon: Clock,      color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
  confirmed:  { icon: Check,      color: 'bg-blue-100 text-blue-800',     label: 'Confirmed' },
  processing: { icon: Package,    color: 'bg-purple-100 text-purple-800', label: 'Processing' },
  shipped:    { icon: Truck,      color: 'bg-indigo-100 text-indigo-800', label: 'Shipped' },
  delivered:  { icon: Check,      color: 'bg-green-100 text-green-800',   label: 'Delivered' },
  cancelled:  { icon: X,          color: 'bg-red-100 text-red-800',       label: 'Cancelled' },
  returned:   { icon: RotateCcw,  color: 'bg-gray-100 text-gray-800',     label: 'Returned' }
};

const returnStatusConfig = {
  return_requested:         { icon: RotateCcw,  label: 'Return Requested',  color: 'bg-yellow-100 text-yellow-800' },
  return_approved:          { icon: Check,       label: 'Return Approved',   color: 'bg-blue-100 text-blue-800' },
  return_product_picked_up: { icon: Truck,       label: 'Product Picked Up', color: 'bg-purple-100 text-purple-800' },
  return_quality_check:     { icon: Search,      label: 'Quality Check',     color: 'bg-indigo-100 text-indigo-800' },
  return_refund_processed:  { icon: ShieldCheck, label: 'Refund Processed',  color: 'bg-green-100 text-green-800' },
  return_rejected:          { icon: X,           label: 'Return Rejected',   color: 'bg-red-100 text-red-800' }
};

const returnSteps = ['return_requested','return_approved','return_product_picked_up','return_quality_check','return_refund_processed'];
const statusSteps = ['pending','confirmed','processing','shipped','delivered'];

const ReturnStatusBadge = ({ returnStatus }) => {
  const rs = returnStatusConfig[returnStatus];
  const RSIcon = rs?.icon || RotateCcw;
  return (
    <Badge className={`${rs?.color || 'bg-orange-100 text-orange-800'} flex items-center gap-1 text-sm px-3 py-1`}>
      <RSIcon className="w-4 h-4" />{rs?.label || returnStatus}
    </Badge>
  );
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { addToCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnDescription, setReturnDescription] = useState('');
  const [selectedReturnItems, setSelectedReturnItems] = useState({}); // { product_id: boolean }
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnHistory, setReturnHistory] = useState([]);
  const [showBill, setShowBill] = useState(false);

  const fetchOrderData = async () => {
    try {
      const response = await authAxios.get(`${API}/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setOrder(response.data);
      if (response.data.return_status) {
        try {
          const historyRes = await authAxios.get(`${API}/orders/${id}/return-history`, { headers: { Authorization: `Bearer ${token}` } });
          setReturnHistory(historyRes.data.return_history || []);
        } catch { setReturnHistory([]); }
      } else {
        setReturnHistory([]);
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchOrderData();
    const interval = setInterval(fetchOrderData, 10000);
    return () => clearInterval(interval);
  }, [id, isAuthenticated, navigate, token]);

  const handleReorder = async () => {
    try {
      for (const item of order.items.filter(i => !i.skipped)) {
        await addToCart(item.product_id, item.quantity);
      }
      toast.success('Items added to cart!');
      navigate('/checkout');
    } catch (error) {
      toast.error('Failed to add some items');
    }
  };

  // Open return dialog — pre-select all returnable (non-skipped) items
  const openReturnDialog = () => {
    const deliveredItems = order.items.filter(i => !i.skipped);
    const initial = {};
    deliveredItems.forEach(item => { initial[item.product_id] = true; });
    setSelectedReturnItems(initial);
    setReturnReason('');
    setReturnDescription('');
    setShowReturnDialog(true);
  };

  const handleReturnRequest = async () => {
    if (!returnReason.trim()) { toast.error('Please enter return reason'); return; }

    const deliveredItems = order.items.filter(i => !i.skipped);
    const chosenItems = deliveredItems.filter(i => selectedReturnItems[i.product_id]);

    if (chosenItems.length === 0) { toast.error('Please select at least one item to return'); return; }

    const returnItemsPayload = chosenItems.map(i => ({
      product_id: i.product_id,
      name: i.name,
      quantity: i.quantity,
      price: i.price
    }));

    setReturnLoading(true);
    try {
      const response = await authAxios.post(
        `${API}/orders/${id}/request-return`,
        { reason: returnReason, description: returnDescription, images: [], return_items: returnItemsPayload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        response.data.is_partial
          ? `Partial return submitted for ${response.data.items_count} item(s)`
          : 'Return request submitted'
      );
      setShowReturnDialog(false);
      await fetchOrderData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error submitting return request');
    } finally {
      setReturnLoading(false);
    }
  };

  const handleRefreshStatus = async () => { await fetchOrderData(); toast.success('Status updated'); };

  const formatPrice = (price) => `₹${price.toFixed(2)}`;
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full"></div></div>;
  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const currentStepIndex = statusSteps.indexOf(order.status);
  const isCancelledOrReturned = ['cancelled', 'returned'].includes(order.status);
  const isReturnRejected = order.return_status === 'return_rejected';
  const hasReturnActivity = !!(order.return_status);
  const isReturnActive = hasReturnActivity && !isReturnRejected;
  const currentReturnStepIndex = returnSteps.indexOf(order.return_status);
  const showReturnButton = (order.status === 'delivered' && !order.return_status) || isReturnRejected;
  const deliveredItems = order.items?.filter(i => !i.skipped) || [];
  const skippedItems = order.items?.filter(i => i.skipped) || [];

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="order-detail-page">
      <div className="container-app">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/orders')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{t('orderDetails')}</h1>
              <p className="text-sm text-slate-500 font-mono">{order.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowBill(true)}>
              <Download className="w-4 h-4 mr-1" />Download Bill
            </Button>
            {(order.status === 'delivered' || order.status === 'returned') && (
              <Button onClick={handleReorder} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-1" />Reorder
              </Button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">

            {/* Status Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${status.color} flex items-center gap-1 text-sm px-3 py-1`}>
                    <StatusIcon className="w-4 h-4" />{status.label}
                  </Badge>
                  {order.has_skipped_items && (
                    <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1 text-sm px-3 py-1">
                      <AlertTriangle className="w-4 h-4" />Partial Delivery
                    </Badge>
                  )}
                  {hasReturnActivity && <ReturnStatusBadge returnStatus={order.return_status} />}
                </div>
                <span className="text-sm text-slate-500">{formatDate(order.created_at)}</span>
              </div>

              {!isCancelledOrReturned && (
                <div className="relative pb-2">
                  <div className="absolute top-4 left-0 right-0 h-0.5 bg-slate-200">
                    <div className="h-full bg-green-500 transition-all" style={{ width: `${(currentStepIndex / (statusSteps.length - 1)) * 100}%` }} />
                  </div>
                  <div className="relative flex justify-between">
                    {statusSteps.map((step, idx) => {
                      const stepCfg = statusConfig[step];
                      const StepIcon = stepCfg.icon;
                      const isActive = idx <= currentStepIndex;
                      return (
                        <div key={step} className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                            <StepIcon className="w-4 h-4" />
                          </div>
                          <span className={`text-xs mt-2 text-center ${isActive ? 'text-green-600 font-medium' : 'text-slate-400'}`}>{stepCfg.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {isReturnRejected && (
                <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-100 flex items-center gap-2">
                  <X className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-700">Your return request was rejected. You can submit a new return request.</p>
                </div>
              )}

              {isReturnActive && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-sm font-semibold text-slate-700">Return Progress</p>
                    {order.is_partial_return && <Badge className="bg-amber-100 text-amber-700 text-xs">Partial Return</Badge>}
                  </div>
                  <div className="relative pb-2">
                    <div className="absolute top-4 left-0 right-0 h-0.5 bg-slate-200">
                      <div className="h-full bg-orange-400 transition-all" style={{ width: currentReturnStepIndex >= 0 ? `${(currentReturnStepIndex / (returnSteps.length - 1)) * 100}%` : '5%' }} />
                    </div>
                    <div className="relative flex justify-between">
                      {returnSteps.map((step, idx) => {
                        const stepCfg = returnStatusConfig[step];
                        const StepIcon = stepCfg.icon;
                        const isActive = currentReturnStepIndex >= 0 ? idx <= currentReturnStepIndex : idx === 0;
                        return (
                          <div key={step} className="flex flex-col items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive ? 'bg-orange-400 text-white' : 'bg-slate-200 text-slate-400'}`}><StepIcon className="w-4 h-4" /></div>
                            <span className={`text-xs mt-2 text-center max-w-[64px] leading-tight ${isActive ? 'text-orange-600 font-medium' : 'text-slate-400'}`}>{stepCfg.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Skipped Items notice */}
            {skippedItems.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-amber-800">Unavailable Items</h3>
                </div>
                <p className="text-sm text-amber-700 mb-3">The following items were unavailable and not included in your delivery. Your total has been adjusted.</p>
                <div className="space-y-2">
                  {skippedItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-white rounded-lg opacity-60">
                      <img src={item.image} alt={item.name} className="w-10 h-10 object-cover rounded grayscale" />
                      <div className="flex-1">
                        <p className="text-sm font-medium line-through text-slate-500">{item.name}</p>
                        <p className="text-xs text-slate-400">{item.quantity} × ₹{item.price} — {item.skip_reason}</p>
                      </div>
                      <span className="text-sm line-through text-slate-400">₹{(item.price * item.quantity).toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Items */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-green-600" />
                {t('items')} ({deliveredItems.length})
              </h2>
              <div className="space-y-4">
                {deliveredItems.map((item, idx) => (
                  <div key={idx} className="flex gap-4 p-3 bg-slate-50 rounded-lg">
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-lg" />
                    <div className="flex-1">
                      <h3 className="font-medium">{item.name}</h3>
                      <p className="text-sm text-slate-500">{item.quantity} x {formatPrice(item.price)}</p>
                      {order.status === 'delivered' && !order.return_status && (
                        <Link to={`/product/${item.product_id}`} className="text-xs text-green-600 hover:text-green-700 mt-1 inline-flex items-center gap-1">
                          <Star className="w-3 h-3" />Rate Product
                        </Link>
                      )}
                    </div>
                    <span className="font-bold text-green-600">{formatPrice(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Return Button */}
            {showReturnButton && deliveredItems.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">Need to return?</h3>
                    <p className="text-sm text-slate-600 mb-4">
                      {isReturnRejected
                        ? 'Your previous return was rejected. You can submit a new return request.'
                        : 'Select which items you want to return.'}
                    </p>
                    <Button onClick={openReturnDialog} variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                      <RotateCcw className="w-4 h-4 mr-2" />Request Return
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Return Timeline */}
            {hasReturnActivity && returnHistory.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold flex items-center gap-2">
                    <RotateCcw className="w-5 h-5 text-orange-600" />Return Timeline
                    {order.is_partial_return && <Badge className="bg-amber-100 text-amber-700 text-xs ml-1">Partial</Badge>}
                  </h2>
                  <Button variant="ghost" size="sm" onClick={handleRefreshStatus}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
                </div>

                {/* Show which items are being returned */}
                {order.return_items?.length > 0 && (
                  <div className="mb-4 p-3 bg-orange-50 rounded-lg">
                    <p className="text-xs font-medium text-orange-700 mb-2">Items in this return:</p>
                    {order.return_items.map((ri, idx) => (
                      <div key={idx} className="flex justify-between text-xs text-orange-600">
                        <span>{ri.name} × {ri.quantity}</span>
                        <span>₹{(ri.price * ri.quantity).toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-4">
                  {returnHistory.map((entry, idx) => {
                    const entryCfg = returnStatusConfig[entry.status];
                    const EntryIcon = entryCfg?.icon || RotateCcw;
                    return (
                      <div key={idx} className="flex gap-3 pb-4 border-b last:border-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${entryCfg?.color || 'bg-orange-100 text-orange-600'}`}>
                          <EntryIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{entryCfg?.label || entry.status}</span>
                            <span className="text-xs text-slate-500">{new Date(entry.timestamp).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          {entry.reason && <p className="text-sm text-slate-600">Reason: {entry.reason}</p>}
                          {entry.description && <p className="text-sm text-slate-600">{entry.description}</p>}
                          {entry.admin_notes && <div className="mt-1 p-2 bg-amber-50 rounded text-sm text-amber-800">Admin Note: {entry.admin_notes}</div>}
                          <p className="text-xs text-slate-400 mt-1">By: {entry.updated_by_name || entry.updated_by}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h2 className="font-semibold mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-green-600" />{t('deliveryAddress')}</h2>
              <p className="text-slate-600">{order.delivery_address}</p>
              <p className="text-slate-600 mt-1">Pincode: {order.delivery_pincode}</p>
              <div className="flex items-center gap-2 mt-3 text-slate-600"><Phone className="w-4 h-4" />{order.phone}</div>
              {order.notes && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-slate-500 mb-1"><FileText className="w-4 h-4" />{t('notes')}</div>
                  <p className="text-slate-600 text-sm">{order.notes}</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h2 className="font-semibold mb-4">Payment Summary</h2>
              <div className="space-y-2">
                {skippedItems.length > 0 && (
                  <div className="flex justify-between text-slate-400 text-sm line-through">
                    <span>Original Total</span>
                    <span>₹{(order.total + skippedItems.reduce((s, i) => s + i.price * i.quantity, 0)).toFixed(2)}</span>
                  </div>
                )}
                {skippedItems.length > 0 && (
                  <div className="flex justify-between text-amber-600 text-sm">
                    <span>Items Skipped</span>
                    <span>-₹{skippedItems.reduce((s, i) => s + i.price * i.quantity, 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-600"><span>Delivery</span><span className="text-green-600">Free</span></div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-100">
                  <span>{t('total')}</span><span className="text-green-600">{formatPrice(order.total)}</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-green-50 rounded-lg text-center"><span className="text-green-700 font-medium">Cash on Delivery</span></div>
              <Button variant="outline" className="w-full mt-4" onClick={() => setShowBill(true)}>
                <Download className="w-4 h-4 mr-2" />Download Bill
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Return Request Dialog with item selection ── */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Request Return</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Item Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Select Items to Return *</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {deliveredItems.map((item) => (
                  <div key={item.product_id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedReturnItems[item.product_id] ? 'border-orange-400 bg-orange-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    onClick={() => setSelectedReturnItems(prev => ({ ...prev, [item.product_id]: !prev[item.product_id] }))}>
                    <input type="checkbox" checked={selectedReturnItems[item.product_id] || false} onChange={() => {}} className="w-4 h-4 text-orange-600 rounded" />
                    <img src={item.image} alt={item.name} className="w-10 h-10 object-cover rounded" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-xs text-slate-500">{item.quantity} × ₹{item.price}</p>
                    </div>
                    <span className="text-sm font-semibold">₹{(item.price * item.quantity).toFixed(0)}</span>
                  </div>
                ))}
              </div>
              {deliveredItems.length > 1 && (
                <div className="flex gap-2 mt-2">
                  <button className="text-xs text-orange-600 hover:underline" onClick={() => { const all = {}; deliveredItems.forEach(i => { all[i.product_id] = true; }); setSelectedReturnItems(all); }}>Select All</button>
                  <span className="text-xs text-slate-400">|</span>
                  <button className="text-xs text-slate-500 hover:underline" onClick={() => setSelectedReturnItems({})}>Clear</button>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="return_reason">Return Reason *</Label>
              <Input id="return_reason" value={returnReason} onChange={(e) => setReturnReason(e.target.value)} placeholder="e.g., Damaged product, Wrong item" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="return_description">Description (Optional)</Label>
              <Textarea id="return_description" value={returnDescription} onChange={(e) => setReturnDescription(e.target.value)} placeholder="Please provide details about your return" className="mt-1" rows={3} />
            </div>

            {/* Return summary */}
            {Object.values(selectedReturnItems).some(Boolean) && (
              <div className="p-3 bg-orange-50 rounded-lg text-sm">
                <p className="font-medium text-orange-700">
                  {deliveredItems.filter(i => selectedReturnItems[i.product_id]).length === deliveredItems.length
                    ? 'Full order return'
                    : `Partial return — ${deliveredItems.filter(i => selectedReturnItems[i.product_id]).length} of ${deliveredItems.length} items`}
                </p>
                <p className="text-orange-600 mt-1">
                  Return value: ₹{deliveredItems.filter(i => selectedReturnItems[i.product_id]).reduce((s, i) => s + i.price * i.quantity, 0).toFixed(2)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnDialog(false)}>Cancel</Button>
            <Button onClick={handleReturnRequest} disabled={returnLoading} className="bg-orange-600 hover:bg-orange-700">
              {returnLoading ? 'Submitting...' : 'Submit Return'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill Dialog */}
      <OrderBill order={order} open={showBill} onClose={() => setShowBill(false)} isAdmin={false} language={'en'} />
    </div>
  );
}
